import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  sendFriendRequest,
  cancelFriendRequest,
  unfriend,
  fetchIncomingFriendRequests,
  fetchOutgoingFriendRequests,
  acceptFriendRequest,
  declineFriendRequest,
} from '../endpoints/api';
import useAuth from './useAuth';

export interface FriendRequestUser {
  id: number;
  name: string;
  profile_photo_url?: string;
}

export interface FriendRequest {
  id: number;
  requester_id: number;
  recipient_id: number;
  status: string;
  created_at: string;
  requester?: FriendRequestUser;
  recipient?: FriendRequestUser;
}

export const useIncomingFriendRequests = () => {
  const {user} = useAuth();
  const userId = user?.id;
  return useQuery<FriendRequest[]>({
    queryKey: ['friendRequests', 'incoming', userId],
    queryFn: async () => {
      const res = await fetchIncomingFriendRequests();
      return (res as any)?.data?.data || (res as any)?.data || [];
    },
    enabled: !!userId,
  });
};

export const useOutgoingFriendRequests = () => {
  const {user} = useAuth();
  const userId = user?.id;
  return useQuery<FriendRequest[]>({
    queryKey: ['friendRequests', 'outgoing', userId],
    queryFn: async () => {
      const res = await fetchOutgoingFriendRequests();
      return (res as any)?.data?.data || (res as any)?.data || [];
    },
    enabled: !!userId,
  });
};

// Helper to optimistically patch the public profile cache for a given user id.
// `profileId` corresponds to the numeric user id used as the second part of
// the ['publicProfile', userId] query key in usePublicProfile.
const patchPublicProfile = (
  queryClient: ReturnType<typeof useQueryClient>,
  profileId: string | number | undefined,
  patch: (prev: any) => any,
) => {
  if (profileId === undefined || profileId === null) return undefined;
  const key = ['publicProfile', String(profileId)];
  const previous = queryClient.getQueryData<any>(key);
  if (previous) {
    queryClient.setQueryData(key, patch(previous));
  }
  return previous;
};

export const useAcceptFriendRequest = () => {
  const queryClient = useQueryClient();
  const {user} = useAuth();
  const userId = user?.id;
  return useMutation({
    mutationFn: (requestId: number) => acceptFriendRequest(requestId),
    onMutate: async (requestId: number) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries(['friendRequests', 'incoming', userId]);

      const previousIncoming = queryClient.getQueryData<FriendRequest[]>([
        'friendRequests',
        'incoming',
        userId,
      ]);

      // Remove from incoming requests optimistically so the row disappears immediately
      if (previousIncoming) {
        queryClient.setQueryData<FriendRequest[]>(
          ['friendRequests', 'incoming', userId],
          previousIncoming.filter((r) => r.id !== requestId),
        );
      }

      // Optimistically flip any matching public profile cache to connected.
      const requesterId = previousIncoming?.find((r) => r.id === requestId)
        ?.requester_id;
      const previousPublicProfile = patchPublicProfile(
        queryClient,
        requesterId,
        (prev) => ({
          ...prev,
          connectionStatus: 'connected',
          friendRequestId: undefined,
          connectionsCount: (prev.connectionsCount ?? 0) + 1,
        }),
      );

      return {previousIncoming, previousPublicProfile, requesterId};
    },
    onError: (_err, _requestId, context) => {
      // Roll back optimistic removal of the incoming request
      if (context?.previousIncoming) {
        queryClient.setQueryData(
          ['friendRequests', 'incoming', userId],
          context.previousIncoming,
        );
      }
      if (context?.previousPublicProfile && context?.requesterId !== undefined) {
        queryClient.setQueryData(
          ['publicProfile', String(context.requesterId)],
          context.previousPublicProfile,
        );
      }
    },
    onSettled: () => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries(['friendRequests']);
      queryClient.invalidateQueries(['connections']);
      queryClient.invalidateQueries(['publicProfile']);
    },
  });
};

export const useDeclineFriendRequest = () => {
  const queryClient = useQueryClient();
  const {user} = useAuth();
  const userId = user?.id;
  return useMutation({
    mutationFn: (requestId: number) => declineFriendRequest(requestId),
    onMutate: async (requestId: number) => {
      await queryClient.cancelQueries(['friendRequests', 'incoming', userId]);
      const previousIncoming = queryClient.getQueryData<FriendRequest[]>([
        'friendRequests',
        'incoming',
        userId,
      ]);
      if (previousIncoming) {
        queryClient.setQueryData<FriendRequest[]>(
          ['friendRequests', 'incoming', userId],
          previousIncoming.filter((r) => r.id !== requestId),
        );
      }

      const requesterId = previousIncoming?.find((r) => r.id === requestId)
        ?.requester_id;
      const previousPublicProfile = patchPublicProfile(
        queryClient,
        requesterId,
        (prev) => ({
          ...prev,
          connectionStatus: 'none',
          friendRequestId: undefined,
        }),
      );

      return {previousIncoming, previousPublicProfile, requesterId};
    },
    onError: (_err, _requestId, context) => {
      if (context?.previousIncoming) {
        queryClient.setQueryData(
          ['friendRequests', 'incoming', userId],
          context.previousIncoming,
        );
      }
      if (context?.previousPublicProfile && context?.requesterId !== undefined) {
        queryClient.setQueryData(
          ['publicProfile', String(context.requesterId)],
          context.previousPublicProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['friendRequests']);
      queryClient.invalidateQueries(['publicProfile']);
    },
  });
};

// `profileId` is the numeric user id of the profile being acted on. It maps to
// the ['publicProfile', userId] query key from usePublicProfile.
export const useSendFriendRequest = (profileId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => sendFriendRequest(userId),
    onMutate: async () => {
      await queryClient.cancelQueries(['publicProfile', profileId]);
      const previousPublicProfile = patchPublicProfile(
        queryClient,
        profileId,
        (prev) => ({
          ...prev,
          connectionStatus: 'pending',
        }),
      );
      return {previousPublicProfile};
    },
    onError: (_err, _vars, context) => {
      if (context?.previousPublicProfile && profileId !== undefined) {
        queryClient.setQueryData(
          ['publicProfile', String(profileId)],
          context.previousPublicProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['friendRequests']);
      queryClient.invalidateQueries(['connections']);
      if (profileId) {
        queryClient.invalidateQueries(['publicProfile', profileId]);
      }
    },
  });
};

export const useCancelFriendRequest = (profileId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => cancelFriendRequest(requestId),
    onMutate: async () => {
      await queryClient.cancelQueries(['publicProfile', profileId]);
      const previousPublicProfile = patchPublicProfile(
        queryClient,
        profileId,
        (prev) => ({
          ...prev,
          connectionStatus: 'none',
          friendRequestId: undefined,
        }),
      );
      return {previousPublicProfile};
    },
    onError: (_err, _vars, context) => {
      if (context?.previousPublicProfile && profileId !== undefined) {
        queryClient.setQueryData(
          ['publicProfile', String(profileId)],
          context.previousPublicProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['friendRequests']);
      queryClient.invalidateQueries(['connections']);
      if (profileId) {
        queryClient.invalidateQueries(['publicProfile', profileId]);
      }
    },
  });
};

export const useUnfriend = (profileId?: string) => {
  const queryClient = useQueryClient();
  const {user} = useAuth();
  const authUserId = user?.id;
  return useMutation({
    mutationFn: (userId: number) => unfriend(userId),
    onMutate: async (unfriendedUserId: number) => {
      await queryClient.cancelQueries(['publicProfile', profileId]);
      const previousPublicProfile = patchPublicProfile(
        queryClient,
        profileId,
        (prev) => ({
          ...prev,
          connectionStatus: 'none',
          friendRequestId: undefined,
          connectionsCount: Math.max(0, (prev.connectionsCount ?? 1) - 1),
        }),
      );

      // Optimistically remove the unfriended user from the connections
      // infinite query so they disappear from MyConnections immediately.
      const previousConnectionsQueries = queryClient.getQueriesData<any>([
        'connections',
        authUserId,
      ]);
      previousConnectionsQueries.forEach(([key, data]) => {
        if (!data?.pages) return;
        queryClient.setQueryData(key, {
          ...data,
          pages: data.pages.map((page: any) => ({
            ...page,
            data: (page.data || []).filter(
              (c: any) => c.id !== unfriendedUserId,
            ),
          })),
        });
      });

      return {previousPublicProfile, previousConnectionsQueries};
    },
    onError: (_err, _vars, context) => {
      if (context?.previousPublicProfile && profileId !== undefined) {
        queryClient.setQueryData(
          ['publicProfile', String(profileId)],
          context.previousPublicProfile,
        );
      }
      context?.previousConnectionsQueries?.forEach(([key, data]: [any, any]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['friendRequests']);
      queryClient.invalidateQueries(['connections']);
      if (profileId) {
        queryClient.invalidateQueries(['publicProfile', profileId]);
      }
    },
  });
};
